import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';

const UIUXCourse = () => {
    useEffect(() => {
        const handleClick = (e) => {
            const a = e.target.closest('a[href^="#"]');
            if (a) {
                const id = a.getAttribute('href');
                if (id.length > 1) {
                    const target = document.querySelector(id);
                    if (target) {
                        e.preventDefault();
                        target.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }
                }
            }
        };
        document.addEventListener('click', handleClick);
        return () => document.removeEventListener('click', handleClick);
    }, []);

    return (
        <>
            <Helmet>
                <title>UI/UX Design with AI — Pyrus Media</title>
                <meta name="description" content="An 8-week, project-first course where you design real interfaces using Figma and AI tools — mentored by the team behind Pyrus Media's live client work." />
                <link rel="preconnect" href="https://fonts.googleapis.com" />
                <link href="https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@400;500;600;700&family=Inter:wght@400;500;600;700&family=JetBrains+Mono:wght@400;500&display=swap" rel="stylesheet" />
            </Helmet>

            <style>{`
                .uiux-page {
                    --canvas: #EDEEF1;
                    --canvas-line: #D7D9DE;
                    --ink: #15171C;
                    --ink-soft: #565A64;
                    --paper: #FBFBFA;
                    --violet: #5B4FE0;
                    --violet-soft: #EDEBFC;
                    --coral: #FF5A36;
                    --green-dot: #1FAE6A;
                }
                .uiux-page { font-family: 'Inter', sans-serif; -webkit-font-smoothing: antialiased; }
                .uiux-page ::selection { background: var(--violet); color: #fff; }
                .uiux-page .font-space { font-family: 'Space Grotesk', sans-serif; }
                .uiux-page .font-mono { font-family: 'JetBrains Mono', monospace; }

                .uiux-grid-bg {
                    background-color: var(--canvas);
                    background-image:
                        linear-gradient(var(--canvas-line) 1px, transparent 1px),
                        linear-gradient(90deg, var(--canvas-line) 1px, transparent 1px);
                    background-size: 48px 48px;
                }

                .uiux-frame-tag {
                    position: absolute; top: 0; left: 0;
                    display: flex; align-items: center; gap: 8px;
                    font-family: 'JetBrains Mono', monospace;
                    font-size: 11.5px; letter-spacing: .02em;
                    color: var(--ink-soft);
                }
                .uiux-frame-tag .dot { width: 6px; height: 6px; background: var(--violet); border-radius: 50%; }

                .uiux-cursor-tag {
                    position: absolute; top: 16px; right: 20px;
                    font-family: 'JetBrains Mono', monospace; font-size: 11px;
                    color: #fff; background: var(--violet);
                    padding: 3px 8px; border-radius: 3px 3px 3px 0;
                }

                .uiux-chip {
                    position: absolute; background: #fff; border: 1px solid var(--canvas-line);
                    border-radius: 8px; padding: 9px 13px; font-size: 12.5px; font-weight: 600;
                    box-shadow: 0 8px 24px rgba(20,20,30,.08);
                    display: flex; align-items: center; gap: 7px;
                }
                .uiux-chip .sw { width: 10px; height: 10px; border-radius: 3px; }

                .uiux-phase-head {
                    display: flex; justify-content: space-between; align-items: center;
                    padding: 20px 26px; border-bottom: 1px solid var(--canvas-line);
                }
                .uiux-week-row {
                    display: grid; grid-template-columns: 90px 1fr; gap: 20px;
                    padding: 18px 26px; border-bottom: 1px solid var(--canvas-line);
                }
                .uiux-week-row:last-child { border-bottom: none; }

                .uiux-price-frame {
                    border: 1.5px solid var(--ink); border-radius: 8px; background: var(--paper);
                    display: grid; grid-template-columns: 1fr 380px;
                }

                .uiux-incl-list li::before { content: "✓"; color: var(--green-dot); font-weight: 700; }

                .uiux-seats-note {
                    font-family: 'JetBrains Mono', monospace; font-size: 12px;
                    background: #fff; border: 1px dashed var(--coral);
                    color: var(--coral); padding: 9px 12px; border-radius: 4px;
                    margin-bottom: 20px; display: inline-block;
                }

                details.uiux-faq { border-bottom: 1px solid var(--canvas-line); padding: 20px 0; }
                summary.uiux-faq-summary {
                    font-family: 'Space Grotesk', sans-serif; font-size: 16.5px; font-weight: 600;
                    cursor: pointer; display: flex; justify-content: space-between; align-items: center;
                    list-style: none;
                }
                summary.uiux-faq-summary::-webkit-details-marker { display: none; }
                summary.uiux-faq-summary::after { content: "+"; font-size: 22px; color: var(--violet); font-weight: 400; }
                details[open] summary.uiux-faq-summary::after { content: "–"; }

                @media (max-width: 900px) {
                    .uiux-chip { display: none; }
                }
                @media (max-width: 860px) {
                    .uiux-price-frame { grid-template-columns: 1fr; }
                }
            `}</style>

            <div className="uiux-page uiux-grid-bg min-h-screen" style={{ color: 'var(--ink)' }}>

                {/* HERO */}
                <section className="px-4 md:px-7 pt-10 pb-16">
                    <div className="max-w-[1180px] mx-auto">
                        <div className="relative border-[1.5px] rounded-md overflow-hidden px-8 md:px-12 pt-14 pb-16" style={{ borderColor: 'var(--ink)', background: 'var(--paper)' }}>

                            <div className="uiux-frame-tag">
                                <span className="dot"></span>Frame — Hero <span className="ml-2">/ 1440×900</span>
                            </div>
                            <div className="uiux-cursor-tag">Pyrus Media</div>

                            <div className="uiux-chip" style={{ top: 60, right: 60 }}>
                                <span className="sw" style={{ background: 'var(--violet)' }}></span>Figma AI · on
                            </div>
                            <div className="uiux-chip hidden md:flex" style={{ top: 150, right: 20 }}>
                                <span className="sw" style={{ background: 'var(--green-dot)' }}></span>Batch 04 open
                            </div>

                            <div className="inline-flex items-center gap-2 font-mono text-xs mb-6 px-3 py-1.5 rounded-full" style={{ color: 'var(--violet)', background: 'var(--violet-soft)' }}>
                                <span className="w-1.5 h-1.5 rounded-full" style={{ background: 'var(--violet)' }}></span>
                                COHORT-BASED · LIVE + PROJECT WORK
                            </div>

                            <h1 className="font-space font-bold text-4xl md:text-[56px] leading-[1.04] tracking-tight mb-5 max-w-[760px]">
                                UI/UX Design,<br />taught the way<br />
                                <span className="italic font-semibold" style={{ color: 'var(--violet)' }}>an agency</span> ships it.
                            </h1>

                            <p className="text-lg leading-relaxed max-w-[560px] mb-8" style={{ color: 'var(--ink-soft)' }}>
                                An 8-week, project-first course where you design real interfaces using Figma and AI tools — user research to shipped prototype — mentored by the team behind Pyrus Media's live client work.
                            </p>

                            <div className="flex flex-wrap gap-3.5 mb-11">
                                <a href="#pricing" className="inline-flex items-center gap-2 font-semibold text-sm px-7 py-3.5 rounded-sm text-white border-[1.5px] transition-transform hover:-translate-y-0.5" style={{ background: 'var(--coral)', borderColor: 'var(--coral)' }}>
                                    Reserve your seat — ₹25,000 →
                                </a>
                                <a href="#curriculum" className="font-semibold text-sm px-7 py-3.5 rounded-sm border-[1.5px]" style={{ borderColor: 'var(--ink)', color: 'var(--ink)' }}>
                                    See the curriculum
                                </a>
                            </div>

                            <div className="flex flex-wrap gap-0 border-t pt-6" style={{ borderColor: 'var(--canvas-line)' }}>
                                <div className="flex-1 min-w-[130px] pr-5">
                                    <div className="font-space text-2xl font-bold">8 wks</div>
                                    <div className="text-xs mt-0.5" style={{ color: 'var(--ink-soft)' }}>2-month live cohort</div>
                                </div>
                                <div className="flex-1 min-w-[130px] pr-5">
                                    <div className="font-space text-2xl font-bold">1</div>
                                    <div className="text-xs mt-0.5" style={{ color: 'var(--ink-soft)' }}>Real client-style capstone</div>
                                </div>
                                <div className="flex-1 min-w-[130px] pr-5">
                                    <div className="font-space text-2xl font-bold">15</div>
                                    <div className="text-xs mt-0.5" style={{ color: 'var(--ink-soft)' }}>Seats per batch, by design</div>
                                </div>
                                <div className="flex-1 min-w-[130px] pr-5">
                                    <div className="font-space text-2xl font-bold">₹25,000</div>
                                    <div className="text-xs mt-0.5" style={{ color: 'var(--ink-soft)' }}>All-inclusive, no hidden fees</div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* WHO IT'S FOR */}
                <section id="who" className="px-4 md:px-7 py-16">
                    <div className="max-w-[1180px] mx-auto">
                        <div className="relative pt-8">
                            <div className="uiux-frame-tag">
                                <span className="dot"></span>Frame — Who it's for / 1440×640
                            </div>
                        </div>
                        <div className="font-mono text-xs tracking-wide mb-2.5" style={{ color: 'var(--violet)' }}>// AUDIENCE</div>
                        <h2 className="font-space font-bold text-[34px] tracking-tight mb-3.5 max-w-[640px]">
                            Built for people who want to ship, not just learn theory.
                        </h2>
                        <p className="text-base leading-[1.7] max-w-[600px] mb-10" style={{ color: 'var(--ink-soft)' }}>
                            Whether you're starting fresh or already working in tech, this course is structured around one outcome: a portfolio-ready, AI-assisted case study you can show in an interview or client pitch.
                        </p>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                            <div className="rounded-md p-6" style={{ background: 'var(--paper)', border: '1px solid var(--canvas-line)' }}>
                                <div className="font-mono text-xs mb-3.5" style={{ color: 'var(--ink-soft)' }}>01 / Beginners</div>
                                <h3 className="font-space text-lg mb-2.5">Graduates &amp; career switchers</h3>
                                <p className="text-[14.5px] leading-relaxed m-0" style={{ color: 'var(--ink-soft)' }}>
                                    No design background needed. You'll learn UX fundamentals and Figma from scratch, with AI tools removing the slow, mechanical parts of the process early on.
                                </p>
                            </div>
                            <div className="rounded-md p-6" style={{ background: 'var(--paper)', border: '1px solid var(--canvas-line)' }}>
                                <div className="font-mono text-xs mb-3.5" style={{ color: 'var(--ink-soft)' }}>02 / IT professionals</div>
                                <h3 className="font-space text-lg mb-2.5">Developers &amp; QA moving into product</h3>
                                <p className="text-[14.5px] leading-relaxed m-0" style={{ color: 'var(--ink-soft)' }}>
                                    If you already work in IT and want to move toward product or design roles, this course gets you a credible, AI-augmented portfolio fast — without quitting your job.
                                </p>
                            </div>
                            <div className="rounded-md p-6" style={{ background: 'var(--paper)', border: '1px solid var(--canvas-line)' }}>
                                <div className="font-mono text-xs mb-3.5" style={{ color: 'var(--ink-soft)' }}>03 / Freelancers</div>
                                <h3 className="font-space text-lg mb-2.5">Marketers &amp; freelancers upgrading skillsets</h3>
                                <p className="text-[14.5px] leading-relaxed m-0" style={{ color: 'var(--ink-soft)' }}>
                                    Add UI/UX and AI-assisted design to your existing service offering — useful if you already do content, marketing, or web work for clients.
                                </p>
                            </div>
                        </div>
                    </div>
                </section>

                {/* CURRICULUM */}
                <section id="curriculum" className="px-4 md:px-7 py-16">
                    <div className="max-w-[1180px] mx-auto">
                        <div className="relative pt-8">
                            <div className="uiux-frame-tag">
                                <span className="dot"></span>Frame — Curriculum / 1440×1200
                            </div>
                        </div>
                        <div className="font-mono text-xs tracking-wide mb-2.5" style={{ color: 'var(--violet)' }}>// 8-WEEK PATH</div>
                        <h2 className="font-space font-bold text-[34px] tracking-tight mb-3.5 max-w-[640px]">
                            Two phases. One shipped project.
                        </h2>
                        <p className="text-base leading-[1.7] max-w-[600px] mb-10" style={{ color: 'var(--ink-soft)' }}>
                            Phase 1 builds your design fundamentals. Phase 2 layers AI into every step of the workflow — research, wireframing, visual design, and testing — the way modern product teams actually work in 2026.
                        </p>

                        {/* Phase 1 */}
                        <div className="rounded-md mb-4 overflow-hidden" style={{ border: '1px solid var(--canvas-line)', background: 'var(--paper)' }}>
                            <div className="uiux-phase-head" style={{ background: 'var(--violet-soft)' }}>
                                <span className="font-space font-semibold text-[17px]">Phase 1 — Design Foundations</span>
                                <span className="font-mono text-[11.5px]" style={{ color: 'var(--violet)' }}>WEEKS 01–04</span>
                            </div>
                            <div className="uiux-week-row">
                                <div className="font-mono text-xs pt-0.5" style={{ color: 'var(--ink-soft)' }}>WK 01</div>
                                <div>
                                    <div className="text-[14.5px] font-semibold mb-1">UX thinking &amp; user research</div>
                                    <div className="text-[13.8px] leading-[1.55]" style={{ color: 'var(--ink-soft)' }}>Design thinking process, user interviews, personas, and how to turn research into problem statements.</div>
                                </div>
                            </div>
                            <div className="uiux-week-row">
                                <div className="font-mono text-xs pt-0.5" style={{ color: 'var(--ink-soft)' }}>WK 02</div>
                                <div>
                                    <div className="text-[14.5px] font-semibold mb-1">Information architecture &amp; wireframing</div>
                                    <div className="text-[13.8px] leading-[1.55]" style={{ color: 'var(--ink-soft)' }}>Sitemaps, user flows, and low-fidelity wireframes in Figma — structure before style.</div>
                                </div>
                            </div>
                            <div className="uiux-week-row">
                                <div className="font-mono text-xs pt-0.5" style={{ color: 'var(--ink-soft)' }}>WK 03</div>
                                <div>
                                    <div className="text-[14.5px] font-semibold mb-1">Visual design systems</div>
                                    <div className="text-[13.8px] leading-[1.55]" style={{ color: 'var(--ink-soft)' }}>Typography, color, spacing, and building a reusable component library in Figma.</div>
                                </div>
                            </div>
                            <div className="uiux-week-row">
                                <div className="font-mono text-xs pt-0.5" style={{ color: 'var(--ink-soft)' }}>WK 04</div>
                                <div>
                                    <div className="text-[14.5px] font-semibold mb-1">Prototyping &amp; usability testing</div>
                                    <div className="text-[13.8px] leading-[1.55]" style={{ color: 'var(--ink-soft)' }}>Interactive prototypes, running your first usability test, and reading the results.</div>
                                </div>
                            </div>
                        </div>

                        {/* Phase 2 */}
                        <div className="rounded-md overflow-hidden" style={{ border: '1px solid var(--canvas-line)', background: 'var(--paper)' }}>
                            <div className="uiux-phase-head" style={{ background: '#FFE9E3' }}>
                                <span className="font-space font-semibold text-[17px]">Phase 2 — AI-Augmented Workflow</span>
                                <span className="font-mono text-[11.5px]" style={{ color: 'var(--coral)' }}>WEEKS 05–08</span>
                            </div>
                            <div className="uiux-week-row">
                                <div className="font-mono text-xs pt-0.5" style={{ color: 'var(--ink-soft)' }}>WK 05</div>
                                <div>
                                    <div className="text-[14.5px] font-semibold mb-1">AI for research &amp; ideation</div>
                                    <div className="text-[13.8px] leading-[1.55]" style={{ color: 'var(--ink-soft)' }}>Using ChatGPT and AI research assistants to synthesize interviews and generate design directions faster.</div>
                                </div>
                            </div>
                            <div className="uiux-week-row">
                                <div className="font-mono text-xs pt-0.5" style={{ color: 'var(--ink-soft)' }}>WK 06</div>
                                <div>
                                    <div className="text-[14.5px] font-semibold mb-1">AI-assisted UI generation</div>
                                    <div className="text-[13.8px] leading-[1.55]" style={{ color: 'var(--ink-soft)' }}>Figma AI, Uizard, and Midjourney for rapid layout exploration and visual concept generation.</div>
                                </div>
                            </div>
                            <div className="uiux-week-row">
                                <div className="font-mono text-xs pt-0.5" style={{ color: 'var(--ink-soft)' }}>WK 07</div>
                                <div>
                                    <div className="text-[14.5px] font-semibold mb-1">Capstone build</div>
                                    <div className="text-[13.8px] leading-[1.55]" style={{ color: 'var(--ink-soft)' }}>Design a full product case study end-to-end, based on a simplified real Pyrus Media client brief.</div>
                                </div>
                            </div>
                            <div className="uiux-week-row">
                                <div className="font-mono text-xs pt-0.5" style={{ color: 'var(--ink-soft)' }}>WK 08</div>
                                <div>
                                    <div className="text-[14.5px] font-semibold mb-1">Portfolio &amp; review</div>
                                    <div className="text-[13.8px] leading-[1.55]" style={{ color: 'var(--ink-soft)' }}>Case-study writeup, portfolio polish, and 1:1 mentor review — presented like a real client pitch.</div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* TOOLS */}
                <section id="tools" className="px-4 md:px-7 py-16">
                    <div className="max-w-[1180px] mx-auto">
                        <div className="relative pt-8">
                            <div className="uiux-frame-tag">
                                <span className="dot"></span>Frame — Toolkit / 1440×320
                            </div>
                        </div>
                        <div className="font-mono text-xs tracking-wide mb-2.5" style={{ color: 'var(--violet)' }}>// WHAT YOU'LL USE</div>
                        <h2 className="font-space font-bold text-[34px] tracking-tight mb-3.5 max-w-[640px]">
                            Figma-first, AI throughout.
                        </h2>
                        <p className="text-base leading-[1.7] max-w-[600px] mb-10" style={{ color: 'var(--ink-soft)' }}>
                            Every tool here is chosen because studios and product teams actually use it in 2026 — not for the sake of a long syllabus.
                        </p>
                        <div className="flex flex-wrap gap-2.5">
                            {[
                                { name: 'Figma', color: '#5B4FE0' },
                                { name: 'Figma AI', color: '#5B4FE0' },
                                { name: 'FigJam', color: '#1FAE6A' },
                                { name: 'Midjourney', color: '#FF5A36' },
                                { name: 'ChatGPT', color: '#1B1D23' },
                                { name: 'Uizard', color: '#2AA9E0' },
                                { name: 'Maze (usability testing)', color: '#E0A22A' },
                            ].map((tool) => (
                                <div key={tool.name} className="flex items-center gap-2 text-[13.5px] font-semibold px-4 py-2.5 rounded-full" style={{ border: '1px solid var(--canvas-line)', background: 'var(--paper)' }}>
                                    <span className="w-2 h-2 rounded-full" style={{ background: tool.color }}></span>
                                    {tool.name}
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* WHY US */}
                <section id="why" className="px-4 md:px-7 py-16">
                    <div className="max-w-[1180px] mx-auto">
                        <div className="relative pt-8">
                            <div className="uiux-frame-tag">
                                <span className="dot"></span>Frame — Why Pyrus Media / 1440×760
                            </div>
                        </div>
                        <div className="font-mono text-xs tracking-wide mb-2.5" style={{ color: 'var(--violet)' }}>// WHY THIS, WHY US</div>
                        <h2 className="font-space font-bold text-[34px] tracking-tight mb-3.5 max-w-[640px]">
                            You're learning from people who ship client work every week.
                        </h2>
                        <p className="text-base leading-[1.7] max-w-[600px] mb-11" style={{ color: 'var(--ink-soft)' }}>
                            Pyrus Media is a working digital studio — not just a training institute. That's the whole point.
                        </p>

                        <div className="grid grid-cols-1 lg:grid-cols-[1.1fr_.9fr] gap-11 items-start">
                            <ul className="list-none m-0 p-0">
                                {[
                                    { num: '01', title: 'Real client-style capstone', desc: 'Your final project is based on a simplified brief drawn from actual Pyrus Media client work — not a generic textbook prompt.' },
                                    { num: '02', title: 'Small batches, real mentorship', desc: 'Capped at 15 seats per cohort so you get direct feedback, not a lecture hall.' },
                                    { num: '03', title: 'AI woven in, not bolted on', desc: 'AI tools are taught inside the actual design process, at the point where they save real time — not as a separate module at the end.' },
                                    { num: '04', title: 'A path into real work', desc: 'Strong capstone projects are considered for freelance or intern opportunities on live Pyrus Media client work.' },
                                ].map((item) => (
                                    <li key={item.num} className="flex gap-4 py-4" style={{ borderBottom: '1px solid var(--canvas-line)' }}>
                                        <div className="w-8 h-8 flex-none rounded-full flex items-center justify-center text-sm font-mono text-white" style={{ background: 'var(--ink)' }}>
                                            {item.num}
                                        </div>
                                        <div>
                                            <h4 className="m-0 mb-1 text-base font-space">{item.title}</h4>
                                            <p className="m-0 text-sm leading-relaxed" style={{ color: 'var(--ink-soft)' }}>{item.desc}</p>
                                        </div>
                                    </li>
                                ))}
                            </ul>

                            <div className="rounded-lg p-8 text-white relative overflow-hidden" style={{ background: 'var(--ink)' }}>
                                <span className="font-mono text-[11.5px] block mb-4" style={{ color: '#B7B2FF' }}>// PYRUS MEDIA</span>
                                <h3 className="font-space text-[22px] m-0 mb-3.5">One studio. Four disciplines.</h3>
                                <p className="text-[14.5px] leading-[1.7] m-0 mb-5" style={{ color: '#C7C8CE' }}>
                                    Beyond this course, Pyrus Media builds digital marketing campaigns, apps, websites, and video content for real businesses — the same team teaching this course runs that work day to day.
                                </p>
                                <div className="flex flex-wrap gap-2">
                                    {['Digital Marketing', 'App Development', 'Web Development', 'Video Editing', 'UI/UX + AI Training'].map((tag) => (
                                        <span key={tag} className="text-xs px-3 py-1.5 rounded-full" style={{ background: 'rgba(255,255,255,.08)', border: '1px solid rgba(255,255,255,.16)' }}>
                                            {tag}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* PRICING */}
                <section id="pricing" className="px-4 md:px-7 py-16">
                    <div className="max-w-[1180px] mx-auto">
                        <div className="relative pt-8">
                            <div className="uiux-frame-tag">
                                <span className="dot"></span>Frame — Enroll / 1440×540
                            </div>
                        </div>
                        <div className="font-mono text-xs tracking-wide mb-2.5" style={{ color: 'var(--violet)' }}>// BATCH 04</div>
                        <h2 className="font-space font-bold text-[34px] tracking-tight mb-3.5 max-w-[640px]">
                            One price. Everything included.
                        </h2>
                        <p className="text-base leading-[1.7] max-w-[600px] mb-10" style={{ color: 'var(--ink-soft)' }}>
                            No surprise add-ons for tool access, mentorship, or the capstone review — it's all part of the ₹25,000 fee.
                        </p>

                        <div className="uiux-price-frame">
                            <div className="p-11" style={{ borderRight: '1px solid var(--canvas-line)' }}>
                                <h3 className="font-space text-2xl m-0 mb-4">UI/UX Design with AI — 2-Month Cohort</h3>
                                <ul className="list-none m-0 p-0 uiux-incl-list">
                                    {[
                                        '8 weeks live, 2 sessions/week (evening & weekend batches)',
                                        'Full Figma + Figma AI, Uizard, Midjourney & ChatGPT workflow training',
                                        '1 real client-style capstone project, mentor-reviewed',
                                        'Small cohort — max 15 seats',
                                        'Portfolio & case-study writeup support',
                                        'Certificate of completion from Pyrus Media',
                                        'Access to recordings for revision',
                                        'Consideration for freelance/intern work on live client projects',
                                    ].map((item) => (
                                        <li key={item} className="flex gap-2.5 items-start py-2 text-[14.5px]">
                                            {item}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                            <div className="p-11 flex flex-col justify-center" style={{ background: 'var(--violet-soft)' }}>
                                <div className="text-base line-through" style={{ color: 'var(--ink-soft)' }}>₹32,000</div>
                                <div className="font-space text-[46px] font-bold my-0.5">₹25,000</div>
                                <div className="text-xs mb-5" style={{ color: 'var(--ink-soft)' }}>2-month course · pay in 2 installments available</div>
                                <div className="uiux-seats-note">Only 15 seats / batch</div>
                                <a href="#" className="block text-center text-white font-bold text-[15.5px] py-4 rounded-sm mb-2.5" style={{ background: 'var(--coral)' }}>
                                    Enroll now — ₹25,000
                                </a>
                                <a href="#" className="block text-center font-semibold text-[14.5px] py-3.5 rounded-sm border-[1.5px]" style={{ borderColor: 'var(--ink)', color: 'var(--ink)' }}>
                                    Chat with us on WhatsApp
                                </a>
                            </div>
                        </div>
                    </div>
                </section>

                {/* FAQ */}
                <section id="faq" className="px-4 md:px-7 py-16">
                    <div className="max-w-[1180px] mx-auto">
                        <div className="relative pt-8">
                            <div className="uiux-frame-tag">
                                <span className="dot"></span>Frame — FAQ / 1440×640
                            </div>
                        </div>
                        <div className="font-mono text-xs tracking-wide mb-2.5" style={{ color: 'var(--violet)' }}>// GOOD TO KNOW</div>
                        <h2 className="font-space font-bold text-[34px] tracking-tight mb-3.5">
                            Frequently asked questions
                        </h2>

                        <details className="uiux-faq" open>
                            <summary className="uiux-faq-summary">Do I need a design background to join?</summary>
                            <p className="text-[14.5px] leading-[1.7] mt-3.5 mb-0" style={{ color: 'var(--ink-soft)' }}>
                                No. The first four weeks are built for complete beginners — we start with UX fundamentals and Figma basics before introducing any AI tools.
                            </p>
                        </details>
                        <details className="uiux-faq">
                            <summary className="uiux-faq-summary">Is this online, offline, or both?</summary>
                            <p className="text-[14.5px] leading-[1.7] mt-3.5 mb-0" style={{ color: 'var(--ink-soft)' }}>
                                Live online sessions with the option to attend in person at our Pune studio for select sessions and the final capstone review. Details are shared after enrollment.
                            </p>
                        </details>
                        <details className="uiux-faq">
                            <summary className="uiux-faq-summary">What if I miss a live session?</summary>
                            <p className="text-[14.5px] leading-[1.7] mt-3.5 mb-0" style={{ color: 'var(--ink-soft)' }}>
                                Every session is recorded and shared with the cohort, so you can catch up without falling behind on the weekly project work.
                            </p>
                        </details>
                        <details className="uiux-faq">
                            <summary className="uiux-faq-summary">Is there a payment plan?</summary>
                            <p className="text-[14.5px] leading-[1.7] mt-3.5 mb-0" style={{ color: 'var(--ink-soft)' }}>
                                Yes — you can pay 60% at enrollment and the remaining 40% by week 4 of the course.
                            </p>
                        </details>
                        <details className="uiux-faq">
                            <summary className="uiux-faq-summary">Will I get placement support?</summary>
                            <p className="text-[14.5px] leading-[1.7] mt-3.5 mb-0" style={{ color: 'var(--ink-soft)' }}>
                                This course is built around a strong, real-world portfolio project rather than a placement guarantee. Top-performing students are considered for freelance or intern work directly with Pyrus Media.
                            </p>
                        </details>
                    </div>
                </section>

            </div>
        </>
    );
};

export default UIUXCourse;
